import React, { useState, useEffect, useCallback } from 'react';
import useUrlState from '../../hooks/useUrlState';
import { useAuth } from '../../auth/AuthProvider';
import Header from '../../components/Header';
import { colors, radius, shadows } from '../../styles/theme';
import { searchMatch } from '../../utils/searchMatch';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const FiyatTakipPage = () => {
  useAuth();

  const [malzemeler, setMalzemeler] = useState([]);
  const [arama, setArama] = useUrlState('arama', '', { debounceMs: 300 });
  const [kategoriFiltre, setKategoriFiltre] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [detay, setDetay] = useState(null);
  const [karsilastirma, setKarsilastirma] = useState([]);
  const [detayLoading, setDetayLoading] = useState(false);
  const [hata, setHata] = useState('');
  const [sortBy, setSortBy] = useState('ad');
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    fetch(`${API_URL}/api/malzemeler`, { headers: getAuthHeaders() })
      .then(res => {
        if (res.status === 401) { window.location.href = '/login'; return []; }
        return res.json();
      })
      .then(data => setMalzemeler(Array.isArray(data) ? data : []))
      .catch(() => setMalzemeler([]));
  }, []);

  const loadDetay = useCallback((malzemeId) => {
    if (!malzemeId) { setDetay(null); setKarsilastirma([]); setHata(''); return; }
    setDetayLoading(true);
    setHata('');

    const headers = getAuthHeaders();

    Promise.all([
      fetch(`${API_URL}/api/fiyat-takip/malzeme/${malzemeId}`, { headers }).then(r => {
        if (r.status === 401) { window.location.href = '/login'; return null; }
        if (!r.ok) throw new Error('Fiyat detayı alınamadı');
        return r.json();
      }),
      fetch(`${API_URL}/api/fiyat-takip/karsilastir?malzeme_id=${malzemeId}`, { headers }).then(r => {
        if (r.status === 401) { window.location.href = '/login'; return null; }
        if (!r.ok) return { karsilastirma: [] };
        return r.json();
      })
    ]).then(([detayData, karsiData]) => {
      setDetay(detayData);
      setKarsilastirma(karsiData?.karsilastirma || []);
      setDetayLoading(false);
    }).catch((err) => {
      logError('Fiyat detay hatası:', err);
      setDetay(null);
      setKarsilastirma([]);
      setHata('Veri yüklenirken hata oluştu. Lütfen tekrar deneyin.');
      setDetayLoading(false);
    });
  }, []);

  const handleSelect = (id) => {
    if (selectedId === id) {
      setSelectedId(null);
      setDetay(null);
      setKarsilastirma([]);
      setHata('');
    } else {
      setSelectedId(id);
      loadDetay(id);
    }
  };

  const kategoriler = [...new Set(malzemeler.map(m => m.kategori).filter(Boolean))].sort();

  const filtered = malzemeler.filter(m => {
    if (arama && !searchMatch(m.ad, arama) && !searchMatch(m.kod, arama)) return false;
    if (kategoriFiltre && m.kategori !== kategoriFiltre) return false;
    return true;
  }).sort((a, b) => {
    let vA = a[sortBy], vB = b[sortBy];
    if (sortBy === 'fiyat') { vA = vA || 0; vB = vB || 0; }
    else { vA = (vA || '').toString().toLowerCase(); vB = (vB || '').toString().toLowerCase(); }
    if (vA < vB) return sortDir === 'asc' ? -1 : 1;
    if (vA > vB) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir(col === 'fiyat' ? 'desc' : 'asc'); }
  };

  const sortIcon = (col) => sortBy === col ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';
  const selectedMalzeme = selectedId ? malzemeler.find(m => m.id === selectedId) : null;

  const exportCSV = () => {
    const rows = [['Kod', 'Malzeme', 'Kategori', 'Birim', 'Güncel Fiyat (TL)']];
    filtered.forEach(m => rows.push([m.kod || '', m.ad, m.kategori || '', m.birim || '', (m.fiyat || 0).toFixed(2)]));
    const csv = rows.map(r => r.join(';')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `fiyat-listesi-${new Date().toISOString().split('T')[0]}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div>
      <Header title="Fiyat Takibi" />
      <div style={{ padding: '20px', background: colors.background, minHeight: 'calc(100vh - 60px)' }}>

        {/* Filtre Barı */}
        <div style={{ background: colors.surface, padding: '14px 20px', borderRadius: radius.lg, marginBottom: '16px', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', boxShadow: shadows.sm }}>
          <input
            type="text" placeholder="Malzeme ara (ad veya kod)..."
            value={arama} onChange={e => setArama(e.target.value)}
            style={{ padding: '9px 14px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '14px', minWidth: '220px' }}
          />
          <select value={kategoriFiltre} onChange={e => setKategoriFiltre(e.target.value)}
            style={{ padding: '9px 14px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '14px' }}>
            <option value="">Tüm Kategoriler</option>
            {kategoriler.map(k => <option key={k} value={k}>{k}</option>)}
          </select>
          <span style={{ fontSize: '13px', color: '#888' }}>{filtered.length} malzeme</span>
          <button onClick={exportCSV} style={{ marginLeft: 'auto', padding: '9px 16px', background: '#1976d2', color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
            CSV İndir
          </button>
        </div>

        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
          {/* Sol: Malzeme Tablosu */}
          <div style={{ flex: selectedId ? '0 0 55%' : '1', background: colors.surface, borderRadius: radius.lg, overflow: 'hidden', boxShadow: shadows.sm, transition: 'flex 0.3s' }}>
            <div style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#1a1a2e', color: 'white', position: 'sticky', top: 0, zIndex: 1 }}>
                    <th style={thS} onClick={() => handleSort('kod')}>Kod{sortIcon('kod')}</th>
                    <th style={thS} onClick={() => handleSort('ad')}>Malzeme{sortIcon('ad')}</th>
                    <th style={thS} onClick={() => handleSort('kategori')}>Kategori{sortIcon('kategori')}</th>
                    <th style={thS} onClick={() => handleSort('birim')}>Birim{sortIcon('birim')}</th>
                    <th style={{ ...thS, textAlign: 'right' }} onClick={() => handleSort('fiyat')}>Güncel Fiyat{sortIcon('fiyat')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr><td colSpan={5} style={{ padding: '30px', textAlign: 'center', color: colors.textTertiary }}>Sonuç bulunamadı</td></tr>
                  ) : filtered.map(m => (
                    <tr key={m.id} onClick={() => handleSelect(m.id)}
                      style={{ borderBottom: '1px solid #f0f0f0', cursor: 'pointer', background: selectedId === m.id ? '#e3f2fd' : 'white', transition: 'background 0.2s' }}>
                      <td style={{ ...tdS, fontFamily: 'monospace', fontSize: '12px', color: '#888' }}>{m.kod || '-'}</td>
                      <td style={{ ...tdS, fontWeight: '500' }}>{m.ad}</td>
                      <td style={tdS}><span style={{ fontSize: '11px', padding: '3px 8px', borderRadius: radius.lg, background: '#f0f0f0', color: '#555' }}>{m.kategori || '-'}</span></td>
                      <td style={{ ...tdS, color: colors.textSecondary }}>{(m.birim || 'KG').toLowerCase()}</td>
                      <td style={{ ...tdS, textAlign: 'right', fontWeight: '700', color: '#1a1a2e' }}>
                        {(m.fiyat || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Sağ: Detay Paneli */}
          {selectedId && (
            <div style={{ flex: '0 0 43%', position: 'sticky', top: '80px' }}>
              {/* Malzeme Özet Kartı */}
              <div style={{ background: colors.surface, borderRadius: radius.lg, padding: '20px', marginBottom: '12px', boxShadow: shadows.sm, borderTop: '3px solid #1976d2' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <h3 style={{ margin: '0 0 4px 0', fontSize: '18px', color: '#1a1a2e' }}>{selectedMalzeme?.ad}</h3>
                    <span style={{ fontSize: '12px', color: '#888' }}>{selectedMalzeme?.kod} | {selectedMalzeme?.kategori} | {(selectedMalzeme?.birim || 'KG').toLowerCase()}</span>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#1976d2' }}>
                      {(selectedMalzeme?.fiyat || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL
                    </div>
                    <div style={{ fontSize: '11px', color: '#888' }}>Güncel referans fiyat</div>
                  </div>
                </div>
                {detay && detay.toplam_kayit > 0 && (
                  <div style={{ display: 'flex', gap: '16px', marginTop: '16px', paddingTop: '12px', borderTop: `1px solid ${colors.border}` }}>
                    <div title="Miktar ağırlıklı ortalama — tüm kayıtlar: Σ(fiyat×miktar) / Σ(miktar)">
                      <div style={{ fontSize: '11px', color: '#888' }}>Ağırlıklı Ort.</div>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: '#333' }}>{(detay.son_3_ortalama || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: '#888' }}>Min</div>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: '#388e3c' }}>{(detay.min_fiyat || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: '#888' }}>Maks</div>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: '#d32f2f' }}>{(detay.max_fiyat || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '11px', color: '#888' }}>Kayıt</div>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: '#555' }}>{detay.toplam_kayit}</div>
                    </div>
                  </div>
                )}
              </div>

              {detayLoading ? (
                <div style={{ background: colors.surface, borderRadius: radius.lg, padding: '30px', textAlign: 'center', color: '#888', boxShadow: shadows.sm }}>Yükleniyor...</div>
              ) : hata ? (
                <div style={{ background: '#fff5f5', borderRadius: radius.lg, padding: '20px', textAlign: 'center', color: '#c62828', fontSize: '13px', boxShadow: shadows.sm, border: '1px solid #ffcdd2' }}>
                  {hata}
                  <br />
                  <button onClick={() => loadDetay(selectedId)} style={{ marginTop: '10px', padding: '6px 16px', background: '#c62828', color: 'white', border: 'none', borderRadius: radius.sm, cursor: 'pointer', fontSize: '12px' }}>
                    Tekrar Dene
                  </button>
                </div>
              ) : (
                <>
                  {/* Tedarikçi Karşılaştırması */}
                  {karsilastirma.length > 0 && (
                    <div style={{ background: colors.surface, borderRadius: radius.lg, overflow: 'hidden', marginBottom: '12px', boxShadow: shadows.sm }}>
                      <div style={{ padding: '12px 16px', background: colors.backgroundAlt, fontWeight: '600', fontSize: '13px', color: '#333', borderBottom: `1px solid ${colors.border}` }}>
                        Tedarikçi Karşılaştırması
                      </div>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: '#fafafa' }}>
                            <th style={thD}>Tedarikçi</th>
                            <th style={{ ...thD, textAlign: 'right' }}>Son Fiyat</th>
                            <th style={{ ...thD, textAlign: 'right' }} title="Son 3 alımın miktar ağırlıklı ortalaması">Son 3 Alım Ort.</th>
                            <th style={{ ...thD, textAlign: 'center' }} title="Bu tedarikçiden toplam fiyat kaydı sayısı">Alım</th>
                          </tr>
                        </thead>
                        <tbody>
                          {karsilastirma.map((k, i) => (
                            <tr key={i} style={{ borderBottom: '1px solid #f0f0f0', background: i === 0 ? '#e8f5e9' : 'white' }}>
                              <td style={{ ...tdD, fontWeight: i === 0 ? '600' : '400' }}>
                                {k.tedarikci_ad} {i === 0 && <span style={{ fontSize: '10px', color: '#388e3c', fontWeight: '700' }}>(EN UYGUN)</span>}
                              </td>
                              <td style={{ ...tdD, textAlign: 'right', fontWeight: '600' }}>{(k.son_fiyat || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL</td>
                              <td style={{ ...tdD, textAlign: 'right', color: colors.textSecondary }}>{(k.ortalama_fiyat || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL</td>
                              <td style={{ ...tdD, textAlign: 'center' }}>{k.alim_sayisi}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Fiyat Geçmişi — TÜM alımlar */}
                  <div style={{ background: colors.surface, borderRadius: radius.lg, overflow: 'hidden', boxShadow: shadows.sm }}>
                    <div style={{ padding: '12px 16px', background: colors.backgroundAlt, fontWeight: '600', fontSize: '13px', color: '#333', borderBottom: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Tüm Satın Alma Geçmişi</span>
                      {detay && detay.son_fiyatlar && (
                        <span style={{ fontSize: '11px', color: '#666', fontWeight: '400' }}>
                          {detay.son_fiyatlar.length} kayıt
                          {detay.son_fiyatlar.length > 0 && (
                            <> • {new Set(detay.son_fiyatlar.map(f => f.tedarikci_ad).filter(Boolean)).size} tedarikçi</>
                          )}
                        </span>
                      )}
                    </div>
                    {(!detay || !detay.son_fiyatlar || detay.son_fiyatlar.length === 0) ? (
                      <div style={{ padding: '24px', textAlign: 'center', color: colors.textTertiary, fontSize: '13px' }}>
                        Bu malzeme için henüz fiyat geçmişi kaydı bulunmuyor.
                        <br /><span style={{ fontSize: '12px' }}>Fiyat Girişi sayfasından alım fişi girildiğinde burada görünecektir.</span>
                      </div>
                    ) : (
                      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                            <tr style={{ background: '#fafafa' }}>
                              <th style={thD}>Tarih</th>
                              <th style={thD}>Tedarikçi</th>
                              <th style={thD}>Fiş / Not</th>
                              <th style={{ ...thD, textAlign: 'right' }}>Fiyat</th>
                              <th style={{ ...thD, textAlign: 'right' }}>Miktar</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detay.son_fiyatlar.map((f, i) => (
                              <tr key={f.id || i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                <td style={{ ...tdD, fontSize: '12px' }}>{f.tarih ? new Date(f.tarih).toLocaleDateString('tr-TR') : '-'}</td>
                                <td style={tdD}>{f.tedarikci_ad || '-'}</td>
                                <td style={{ ...tdD, fontSize: '11px', color: colors.textSecondary, maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.aciklama || ''}>
                                  {f.aciklama || '-'}
                                </td>
                                <td style={{ ...tdD, textAlign: 'right', fontWeight: '600' }}>{(f.birim_fiyat || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} TL</td>
                                <td style={{ ...tdD, textAlign: 'right', color: colors.textSecondary }}>{f.miktar}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const thS = { padding: '10px 14px', textAlign: 'left', fontSize: '12px', fontWeight: '600', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' };
const tdS = { padding: '10px 14px', fontSize: '13px' };
const thD = { padding: '8px 12px', textAlign: 'left', fontSize: '11px', fontWeight: '600', color: '#888', textTransform: 'uppercase' };
const tdD = { padding: '8px 12px', fontSize: '13px' };

export default FiyatTakipPage;
